import { Maybe } from "graphql";
import includes from "lodash/includes";
import uniq from "lodash/uniq";

import { features } from "utils/feature-toggle/Decision";
import { applyTax } from "utils/price.utils";
import { initializeApollo } from "utils/with-apollo.utils";

import {
  AvailableCatalogTagsDocument,
  AvailableCatalogTagsQuery,
} from "./available-catalog-tags.query.generated";
import {
  AvailableCatalogDocument,
  AvailableCatalogQuery,
} from "./available-catalog.query.generated";

type AvailableCatalogEntry = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<AvailableCatalogQuery["availableCatalog"]>["data"]
    >["attributes"]
  >["entries"]
>[number];

type AvailableUpfront = AvailableCatalogQuery["upfronts"][number];

export type CatalogEntryDetails = {
  modelName: string;
  brand: string;
  isNew: boolean;
  isPreOrder: boolean;
  isOutOfStock: boolean | undefined;
  minimumSubscriptionPrice: number;
  miniumSubscriptionCommitmentPeriod: number;
  minimumUpfrontPrice: number;
  originalUpfrontPrice: number | null;
  originalSubscriptionPrice: number | null;
  availableColors: string[];
  availableStorage: string[];
  picture: {
    alternativeText: Maybe<string>;
    name?: string;
    medium?: string;
    thumbnail?: string;
  };
  slug: string;
  tagSlugs: (string | undefined)[] | undefined;
};

export const getAvailableCatalogTags = async () => {
  const apolloClient = initializeApollo();

  const { data } = await apolloClient.query<AvailableCatalogTagsQuery>({
    query: AvailableCatalogTagsDocument,
  });

  return (
    data.tags?.data
      ?.map(({ attributes }) => ({
        name: attributes?.name ?? "",
        slug: attributes?.slug ?? "",
        title: attributes?.title ?? "",
        description: attributes?.description ?? "",
        isCatalogFilter: attributes?.isCatalogFilter ?? false,
      }))
      .sort(({ name: nameLeft }, { name: nameRight }) =>
        nameLeft.localeCompare(nameRight),
      ) ?? []
  );
};

export const getAvailableEntries = async () => {
  const apolloClient = initializeApollo();

  const { data } = await apolloClient.query<AvailableCatalogQuery>({
    query: AvailableCatalogDocument,
  });

  const isBlackFriday =
    data?.websiteSetting?.data?.attributes?.isBlackFriday ?? false;

  return data.availableCatalog?.data?.attributes?.entries?.map((entry) =>
    catalogEntryDetails({
      catalogueEntry: entry,
      entryUpfronts: data?.upfronts,
      isBlackFriday,
    }),
  );
};

export const catalogEntryDetails = ({
  catalogueEntry,
  entryUpfronts,
  isBlackFriday = false,
}: {
  catalogueEntry: AvailableCatalogEntry;
  entryUpfronts: AvailableUpfront[];
  isBlackFriday?: boolean;
}): CatalogEntryDetails => {
  const subscriptions = catalogueEntry?.subscriptions?.data;

  const shouldIgnoreOutOfStock = features.ignoreOutOfStock(
    subscriptions?.at(0)?.attributes?.subscriptionItem?.properties?.model ?? "",
  );

  const isOutOfStock = shouldIgnoreOutOfStock
    ? false
    : subscriptions?.every((subscription) => {
        const isOnline = Boolean(
          subscription?.attributes?.subscriptionItem?.stock?.isOnline,
        );
        const isMustBePurchased = Boolean(
          subscription?.attributes?.subscriptionItem?.properties
            ?.mustBePurchased,
        );
        const isRestrictedModel = features.isRestrictedModel(
          subscription?.attributes?.subscriptionItem?.properties?.model,
        );

        // For iPhone 17/Air models, ignore isMustBePurchased and only check isOnline
        return isRestrictedModel ? !isOnline : !isOnline && !isMustBePurchased;
      });

  const availableModels = subscriptions?.map(
    (subscription) =>
      subscription?.attributes?.subscriptionItem?.properties?.model,
  );

  const availableUpfronts = entryUpfronts.filter(({ properties }) =>
    includes(availableModels, properties?.model),
  );

  const minimumSubscriptionPrice = Math.min(
    ...(subscriptions
      ?.map((subscription) => subscription?.attributes?.subscriptionItem)
      ?.map((subscriptionItem) =>
        applyTax(
          subscriptionItem?.priceExcludingTax ?? undefined,
          subscriptionItem?.tax?.amount,
        ),
      ) ?? [0]),
  );
  const miniumSubscriptionCommitmentPeriod =
    subscriptions?.find(
      (subscription) =>
        applyTax(
          subscription?.attributes?.subscriptionItem?.priceExcludingTax ??
            undefined,
          subscription?.attributes?.subscriptionItem?.tax?.amount,
        ) === minimumSubscriptionPrice,
    )?.attributes?.subscriptionItem?.properties.contractTerm || 0;

  const minimumUpfrontPrice = Math.min(
    ...availableUpfronts?.map(({ priceExcludingTax, tax }) =>
      applyTax(priceExcludingTax ?? undefined, tax?.amount),
    ),
  );

  const flagshipVariant =
    catalogueEntry?.flagship?.data?.attributes?.smartphoneVariant?.data
      ?.attributes;
  const flagshipPicture = flagshipVariant?.combinedPicture?.data?.attributes;
  const flagshipPictureText = flagshipPicture?.alternativeText;
  const flagshipPictureFormats = flagshipPicture?.formats;

  const picture = {
    alternativeText: flagshipPictureText ?? null,
    medium: flagshipPictureFormats?.medium?.url ?? null,
    thumbnail: flagshipPictureFormats?.thumbnail?.url ?? null,
  };

  const brand = flagshipVariant?.smartphoneFamily?.data?.attributes?.brand;

  const slug = `/${flagshipVariant?.smartphoneFamily?.data?.attributes?.slug}`;

  const availableColors = uniq(
    subscriptions?.map(
      ({ attributes }) =>
        attributes?.smartphoneVariant?.data?.attributes?.colorCode ?? "",
    ),
  );

  const availableStorage = uniq(
    subscriptions?.map(
      ({ attributes }) =>
        attributes?.smartphoneVariant?.data?.attributes?.storage ?? "",
    ),
  );

  const tagSlugs = catalogueEntry?.tags?.data?.map(
    ({ attributes }) => attributes?.slug,
  );

  const drops = catalogueEntry?.subscriptionDrop ?? [];
  const maxOriginalSubscriptionDropPrice = Math.max(
    ...drops
      .filter((drop): drop is NonNullable<typeof drop> => drop != null)
      .map((drop) => drop.formerSubscriptionPriceInCentsIncludingTax)
      .filter((v): v is number => typeof v === "number"),
  );
  const maxOriginalUpfrontDropPrice = Math.max(
    ...drops
      .filter((drop): drop is NonNullable<typeof drop> => drop != null)
      .map((drop) => drop.formerUpfrontPriceInCentsIncludingTax)
      .filter((v): v is number => typeof v === "number"),
  );

  const originalUpfrontPrice =
    maxOriginalUpfrontDropPrice ??
    (isBlackFriday
      ? catalogueEntry?.blackFridayFormerUpfrontPriceInCentsInclundingTax
      : catalogueEntry?.formerUpfrontPriceInCentsInclundingTax) ??
    null;

  const originalSubscriptionPrice = maxOriginalSubscriptionDropPrice ?? null;

  return {
    modelName: catalogueEntry?.entryName ?? "",
    brand: brand ?? "",
    isNew: catalogueEntry?.isNew || false,
    isPreOrder: catalogueEntry?.isPreOrder || false,
    isOutOfStock,
    minimumSubscriptionPrice,
    miniumSubscriptionCommitmentPeriod,
    minimumUpfrontPrice,
    originalUpfrontPrice,
    availableColors,
    availableStorage,
    picture,
    slug,
    tagSlugs,
    originalSubscriptionPrice,
  };
};
